import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Mail, Phone, MapPin } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

/** Sahel Moderne — tons terre / terracotta, layout contemporain. */
const SahelModernTemplate: React.FC<TemplateProps> = ({
    data,
    isContinuationPage = false,
    showPlaceholders = false,
    pageIndex = 0,
    fullData,
}) => {
    const primaryColor = data.style?.primaryColor || '#9a3412';
    const {
        fontFamily = 'font-sans',
        textColor = '#1c1917',
        backgroundColor = '#fff7ed',
        sectionBackgroundColor = '#7c2d12',
    } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const interestsContinued = isSectionContinuation(source, 'interests', pageIndex);

    const phone = data.personalInfo.phone || (showPlaceholders ? '+225 01 00 00 00 00' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'hello@email.ci' : '');
    const address = data.personalInfo.address || (showPlaceholders ? 'Bouake, Cote dIvoire' : '');
    const summary =
        data.personalInfo.summary ||
        (showPlaceholders
            ? 'Profil polyvalent, creatif et engage, a l aise dans des environnements exigeants.'
            : '');
    const photoSrc = data.personalInfo.profileImage || '';

    const skills: any[] =
        data.skills && data.skills.length
            ? data.skills
            : showPlaceholders
              ? [{ name: 'Creativite' }, { name: 'Terrain' }, { name: 'Teamwork' }]
              : [];
    const interests: any[] =
        data.interests && data.interests.length
            ? data.interests
            : showPlaceholders
              ? [{ name: 'Sport' }, { name: 'Lecture' }]
              : [];
    const experience: any[] =
        data.experience && data.experience.length
            ? data.experience
            : showPlaceholders
              ? [
                    {
                        position: 'Chargee de projets',
                        company: 'ONG Locale',
                        startDate: '2022',
                        endDate: '2025',
                        description: 'Coordination d activites terrain et reporting.',
                        isCurrent: false,
                    },
                ]
              : [];
    const education: any[] =
        data.education && data.education.length
            ? data.education
            : showPlaceholders
              ? [
                    {
                        degree: 'Licence',
                        field: 'Sociologie',
                        institution: 'UAO',
                        startDate: '2018',
                        endDate: '2021',
                        isCurrent: false,
                    },
                ]
              : [];

    return (
        <div
            className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full flex overflow-hidden ${fontFamily}`}
            style={{ backgroundColor, color: textColor }}
        >
            <div
                data-cv-sidebar
                className="w-[30%] text-orange-50 p-7 flex flex-col overflow-hidden"
                style={{ backgroundColor: sectionBackgroundColor }}
            >
                {isContinuationPage ? (
                    <div>
                        <p className="cv-fs-name text-xs font-bold uppercase tracking-widest opacity-80">
                            {data.personalInfo.firstName} {data.personalInfo.lastName}
                        </p>
                        <p className="text-[10px] mt-2 opacity-50">{CV_LABELS.suite}</p>
                    </div>
                ) : (
                    <>
                        {(photoSrc || showPlaceholders) && (
                            <div className="mb-5">
                                <div className="w-full aspect-square rounded-2xl overflow-hidden bg-orange-900/40">
                                    {photoSrc ? (
                                        <img src={photoSrc} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full bg-orange-900/60" />
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="cv-fs-contact space-y-3 text-xs mb-8">
                            {phone && (
                                <div className="flex gap-2">
                                    <Phone className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>{phone}</span>
                                </div>
                            )}
                            {email && (
                                <div className="flex gap-2">
                                    <Mail className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span className="break-all">{email}</span>
                                </div>
                            )}
                            {address && (
                                <div className="flex gap-2">
                                    <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>{address}</span>
                                </div>
                            )}
                        </div>
                    </>
                )}

                <section className="mb-6">
                    <SectionHeading
                        title={CV_LABELS.skills}
                        isContinuation={skillsContinued}
                        primaryColor="#fed7aa"
                        variant="bar"
                    />
                    <div className="cv-fs-skills flex flex-wrap gap-1.5 mt-3">
                        {skills.map((s, i) => (
                            <span
                                key={s.id || i}
                                data-cv-block
                                data-cv-item-id={s.id || undefined}
                                className="text-[10px] px-2 py-1 rounded-full bg-white/10"
                            >
                                {s.name}
                            </span>
                        ))}
                    </div>
                </section>

                <section>
                    <SectionHeading
                        title={CV_LABELS.interests}
                        isContinuation={interestsContinued}
                        primaryColor="#fed7aa"
                        variant="bar"
                    />
                    <div className="cv-fs-interests space-y-1 mt-2">
                        {interests.map((it, i) => (
                            <div
                                key={it.id || i}
                                data-cv-block
                                data-cv-item-id={it.id || undefined}
                                className="text-xs"
                            >
                                {it.name}
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            <div data-cv-main className="flex-1 p-9 overflow-hidden">
                {!isContinuationPage && (
                    <header className="mb-6">
                        <h1
                            className="cv-fs-name text-4xl font-black tracking-tight leading-none"
                            style={{ color: primaryColor }}
                        >
                            {data.personalInfo.firstName || (showPlaceholders ? 'Aminata' : '')}
                            <br />
                            {data.personalInfo.lastName || (showPlaceholders ? 'Kone' : '')}
                        </h1>
                        <p className="mt-2 text-sm font-medium uppercase tracking-[0.15em] text-stone-500">
                            {data.personalInfo.profession ||
                                (showPlaceholders ? 'Chargee de projets' : '')}
                        </p>
                        {summary && (
                            <p
                                className="cv-fs-summary mt-4 text-sm leading-relaxed text-stone-600 border-l-4 pl-3"
                                style={{ borderColor: primaryColor }}
                            >
                                {summary}
                            </p>
                        )}
                    </header>
                )}

                <section className="mb-6">
                    <SectionHeading
                        title={CV_LABELS.experience}
                        isContinuation={expContinued}
                        primaryColor={primaryColor}
                        variant="bar"
                    />
                    <div className="cv-fs-experience space-y-4 mt-3">
                        {experience.map((exp, i) => (
                            <div key={exp.id || i} data-cv-block data-cv-item-id={exp.id || undefined}>
                                <p className="font-bold text-sm">{exp.position}</p>
                                <p className="text-xs font-semibold" style={{ color: primaryColor }}>
                                    {exp.company} · {exp.startDate} —{' '}
                                    {frPresent(!!exp.isCurrent, exp.endDate || '')}
                                </p>
                                {exp.description && (
                                    <p className="text-xs mt-1 text-stone-600">{exp.description}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                <section>
                    <SectionHeading
                        title={CV_LABELS.education}
                        isContinuation={eduContinued}
                        primaryColor={primaryColor}
                        variant="bar"
                    />
                    <div className="cv-fs-education space-y-3 mt-3">
                        {education.map((edu, i) => (
                            <div key={edu.id || i} data-cv-block data-cv-item-id={edu.id || undefined}>
                                <p className="font-bold text-sm">
                                    {edu.degree}
                                    {edu.field ? ` — ${edu.field}` : ''}
                                </p>
                                <p className="text-xs text-stone-600">
                                    {edu.institution} · {edu.startDate} —{' '}
                                    {frPresent(!!edu.isCurrent, edu.endDate || '')}
                                </p>
                                {edu.description && (
                                    <p className="text-xs mt-1 text-stone-600">{edu.description}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default SahelModernTemplate;
